// Nadrama <https://nadrama.com>
// Copyright Nadrama Pty Ltd
// All rights reserved.
import {
  For,
  Show,
  createContext,
  createSignal,
  useContext,
  type Accessor,
} from "solid-js";
import Plus from "lucide-solid/icons/plus";
import Trash2 from "lucide-solid/icons/trash-2";
import countriesLocale from "i18n-iso-countries/langs/en.json";
import type { ZodMiniType } from "zod/mini";
import { apiErrorMessage } from "../api/runtime";
import { formObject } from "../form-validation";

const FormErrors = createContext<Accessor<Record<string, string>>>();

function useFormError(name: string) {
  const errors = useContext(FormErrors);
  return () => errors?.()[name] ?? "";
}

export function ValidationMessage(p: { name: string }) {
  const error = useFormError(p.name);
  return (
    <Show when={error()}>
      <p
        id={`${p.name}-error`}
        aria-live="polite"
        class="validation-error mt-1 text-sm"
      >
        {error()}
      </p>
    </Show>
  );
}

const countries = Object.entries(countriesLocale.countries)
  .map(
    ([code, names]) =>
      [code, Array.isArray(names) ? names[0]! : names] as const,
  )
  .sort((a, b) => a[1].localeCompare(b[1]));
export function Button(p: {
  children: unknown;
  onClick?: () => void;
  kind?: string;
  type?: "button" | "submit";
  disabled?: boolean;
}) {
  return (
    <button
      type={p.type ?? "button"}
      onClick={p.onClick}
      disabled={p.disabled}
      class={`btn ${p.kind ?? "btn-primary"}`}
    >
      {p.children as never}
    </button>
  );
}
export function Form<T extends Record<string, unknown> = Record<string, string>>(props: {
  children: unknown;
  action: (data: T) => Promise<void>;
  schema: ZodMiniType;
  value?: (data: FormData) => T;
  actions?: (busy: boolean) => unknown;
  submitLabel?: string;
  successMessage?: string;
  resetOnSuccess?: boolean;
}) {
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal("");
  const [success, setSuccess] = createSignal("");
  const [fieldErrors, setFieldErrors] = createSignal<Record<string, string>>({});
  const [submitted, setSubmitted] = createSignal(false);
  let form!: HTMLFormElement;
  const value = (data: FormData) =>
    props.value ? props.value(data) : (formObject(data) as T);
  const issueField = (issue: { path: PropertyKey[] }) =>
    typeof issue.path[0] === "string" ? issue.path[0] : "";
  const matches = (field: string, issue: string) =>
    field === issue ||
    (field.startsWith("notification_email_") && issue === "notification_emails");
  const controlNames = () =>
    Array.from(form.elements)
      .filter(
        (element): element is HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement =>
          element instanceof HTMLInputElement ||
          element instanceof HTMLSelectElement ||
          element instanceof HTMLTextAreaElement,
      )
      .map((element) => element.name)
      .filter(Boolean);
  const errorsFor = (
    names: string[],
    issues: ReadonlyArray<{ message: string; path: PropertyKey[] }>,
  ) =>
    Object.fromEntries(
      names.map((name) => {
        const issue = issues.find((candidate) =>
          matches(name, issueField(candidate)),
        );
        if (!issue) return [name, ""];
        if (issue.message !== "Invalid input") return [name, issue.message];
        const control = form.elements.namedItem(name);
        return [
          name,
          control instanceof HTMLInputElement ||
          control instanceof HTMLSelectElement ||
          control instanceof HTMLTextAreaElement
            ? control.value
              ? "Enter a valid value."
              : "This field is required."
            : "Enter a valid value.",
        ];
      }),
    );
  const distinctIssues = <T extends { message: string }>(issues: ReadonlyArray<T>) =>
    issues.filter(
      (candidate, index, all) =>
        candidate.message !== "Public and private subnets must not overlap." ||
        all.findIndex((item) => item.message === candidate.message) === index,
    );
  const setErrors = (errors: Record<string, string>) => {
    setFieldErrors(errors);
    for (const element of Array.from(form.elements)) {
      if (
        !(element instanceof HTMLInputElement) &&
        !(element instanceof HTMLSelectElement) &&
        !(element instanceof HTMLTextAreaElement)
      )
        continue;
      const invalid = Boolean(errors[element.name]);
      element.classList.toggle("validation-control-error", invalid);
      element.setAttribute("aria-invalid", String(invalid));
      if (invalid)
        element.setAttribute("aria-describedby", `${element.name}-error`);
      else element.removeAttribute("aria-describedby");
    }
  };
  const validateField = (target: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement) => {
    if (!target.name) return;
    const result = props.schema.safeParse(value(new FormData(form)));
    const issues = result.success ? [] : distinctIssues(result.error.issues);
    const names = submitted()
      ? controlNames()
      : [...new Set([...Object.keys(fieldErrors()), target.name])];
    setErrors(errorsFor(names, issues));
  };
  return (
    <form
      ref={form}
      class="grid gap-5"
      noValidate
      onInput={(event) => {
        const target = event.target;
        if (
          target instanceof HTMLInputElement ||
          target instanceof HTMLSelectElement ||
          target instanceof HTMLTextAreaElement
        )
          validateField(target);
      }}
      onChange={(event) => {
        const target = event.target;
        if (
          target instanceof HTMLInputElement ||
          target instanceof HTMLSelectElement ||
          target instanceof HTMLTextAreaElement
        )
          validateField(target);
      }}
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        setSuccess("");
        const data = new FormData(e.currentTarget);
        if (e.submitter instanceof HTMLButtonElement && e.submitter.name)
          data.set(e.submitter.name, e.submitter.value);
        const validated = props.schema.safeParse(value(data));
        if (!validated.success) {
          setSubmitted(true);
          const issue = validated.error.issues[0];
          const names = controlNames();
          const field = issue
            ? names.find((name) => matches(name, issueField(issue))) ?? ""
            : "";
          const issues = distinctIssues(validated.error.issues);
          setErrors(errorsFor(names, issues));
          const element =
            field ? form.elements.namedItem(field) : null;
          if (element instanceof HTMLElement) element.focus();
          setError(
            field
              ? ""
              : (issue?.message ?? "Check the highlighted form values."),
          );
          setBusy(false);
          return;
        }
        try {
          await props.action(value(data));
          setSubmitted(false);
          setErrors({});
          if (props.resetOnSuccess) form.reset();
          if (props.successMessage) setSuccess(props.successMessage);
        } catch (x) {
          setError(apiErrorMessage(x));
        } finally {
          setBusy(false);
        }
      }}
    >
      <FormErrors.Provider value={fieldErrors}>
        {props.children as never}
        <Show when={error()}>
          <div role="alert" class="alert alert-error">
            {error()}
          </div>
        </Show>
        <Show when={success()}>
          <div role="status" class="alert alert-success">
            {success()}
          </div>
        </Show>
        {props.actions ? (
          (props.actions(busy()) as never)
        ) : (
          <button disabled={busy()} class="btn btn-primary" type="submit">
            {busy() ? "Working…" : (props.submitLabel ?? "Save and continue")}
          </button>
        )}
      </FormErrors.Provider>
    </form>
  );
}
function NotificationEmailFields(p: { values?: string[] | null }) {
  const [values, setValues] = createSignal(
    p.values?.length ? [...p.values] : [""],
  );
  return (
    <fieldset class="field">
      <div class="flex items-center justify-between">
        <legend class="font-semibold">Notification emails</legend>
        <button
          type="button"
          class="icon-action"
          aria-label="Add notification email"
          title="Add notification email"
          onClick={() => setValues([...values(), ""])}
        >
          <Plus size={18} />
        </button>
      </div>
      <For each={values()}>
        {(value, index) => (
          <div class="grid gap-1">
            <div class="flex items-center gap-2">
              <input
                class="input input-bordered min-w-0 flex-1"
                aria-label={`Notification email ${index() + 1}`}
                name={`notification_email_${index()}`}
                value={value}
                type="email"
                required
                maxLength={254}
                onInput={(event) =>
                  setValues((current) =>
                    current.map((item, itemIndex) =>
                      itemIndex === index() ? event.currentTarget.value : item,
                    ),
                  )
                }
              />
              <Show when={values().length > 1}>
                <button
                  type="button"
                  class="icon-action shrink-0 text-rose-700"
                  aria-label={`Remove notification email ${index() + 1}`}
                  title="Remove notification email"
                  onClick={() =>
                    setValues((current) =>
                      current.filter((_, itemIndex) => itemIndex !== index()),
                    )
                  }
                >
                  <Trash2 size={18} />
                </button>
              </Show>
            </div>
            <ValidationMessage name={`notification_email_${index()}`} />
          </div>
        )}
      </For>
      <p class="help">
        Operational notifications will be sent to each address.
      </p>
    </fieldset>
  );
}
export function Field(p: {
  name: string;
  label: string;
  help?: string;
  value?: string | string[] | null;
  placeholder?: string;
  type?: string;
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  min?: number;
  max?: number;
  multiple?: boolean;
}) {
  const error = useFormError(p.name);
  const initialValue = typeof p.value === "string" ? p.value : "";
  if (p.name === "notification_emails")
    return (
      <NotificationEmailFields
        values={Array.isArray(p.value) ? p.value : undefined}
      />
    );
  if (p.name === "country")
    return (
      <div class="field">
        <label for={p.name}>{p.label}</label>
        <select
          class="select select-bordered w-full"
          id={p.name}
          name={p.name}
          value={typeof p.value === "string" ? p.value : ""}
          required={p.required}
          aria-invalid={Boolean(error())}
          aria-describedby={error() ? `${p.name}-error` : undefined}
        >
          <option value="" disabled>
            Select country...
          </option>
          <For each={countries}>
            {(country) => <option value={country[0]}>{country[1]}</option>}
          </For>
        </select>
        <Show when={p.help}>
          <p class="help">{p.help}</p>
        </Show>
        <ValidationMessage name={p.name} />
      </div>
    );
  return (
    <div class="field">
      <label for={p.name}>{p.label}</label>
      <input
        class="input input-bordered w-full"
        id={p.name}
        name={p.name}
        value={initialValue}
        placeholder={p.placeholder}
        type={p.type ?? "text"}
        required={p.required}
        minLength={p.minLength}
        maxLength={p.maxLength}
        pattern={p.pattern}
        min={p.min}
        max={p.max}
        multiple={p.multiple}
        aria-invalid={Boolean(error())}
        aria-describedby={error() ? `${p.name}-error` : undefined}
      />
      <Show when={p.help}>
        <p class="help">{p.help}</p>
      </Show>
      <ValidationMessage name={p.name} />
    </div>
  );
}
